import { Box, Icon, Flex, Divider, FormLabel, Text } from '@chakra-ui/react';
import { useFormikContext } from 'formik';
import { HiOutlineQuestionMarkCircle } from 'react-icons/hi';

import { FormikDatepicker, FormikInput, FormikRadio, FormikSelect, FormikSwitch } from 'components/Formik';
import Tooltip from 'components/Tooltip';

import { initialCategoryFormData } from '../schema';
import CronExpressionGenerator from './CronExpressionGenerator';

const Accrual = () => {
  const { values } = useFormikContext<typeof initialCategoryFormData>();

  return (
    <Box
      as="fieldset"
      disabled={values.id ? true : false}
      pointerEvents={values.id ? 'none' : 'auto'}
      opacity={values.id ? 0.6 : 1}
    >
      <CronExpressionGenerator />
      <Divider my="6" borderBottomWidth="2px" borderStyle="dashed" />

      <FormikRadio
        label={
          <Flex alignItems="center">
            Waiting Period Before Accrual
            <Tooltip label="Defines the waiting period before employees can start accruing time.">
              <Box ml="2">
                <Icon as={HiOutlineQuestionMarkCircle} height="18px" width="18px" color="gray.400" />
              </Box>
            </Tooltip>
          </Flex>
        }
        options={[
          { label: 'No waiting period, begin accruing time immediately', value: '0' },
          { label: 'There is a waiting period (You’ll set this date below)', value: '1' },
        ]}
        radioProps={{
          colorScheme: 'secondary',
        }}
        name="set_waiting_period"
      />
      {values.set_waiting_period === '1' && (
        <Flex gridGap="2" mb="7" pl="6">
          <Text mt="4">Start accruing time after</Text>
          <FormikDatepicker
            name="accrue_start"
            label=""
            formControlProps={{ maxWidth: '200px', mb: 0 }}
            datepickerProps={{ isClearable: false, minDate: new Date() }}
          />
        </Flex>
      )}
      <FormikRadio
        label={
          <Flex alignItems="center">
            Cap on employee accrual balance
            <Tooltip
              label={`Specifies a limit on the total number of leave ${
                values.in_hours === 'false' ? 'days' : 'hours'
              } an employee can accumulate.`}
            >
              <Box ml="2">
                <Icon as={HiOutlineQuestionMarkCircle} height="18px" width="18px" color="gray.400" />
              </Box>
            </Tooltip>
          </Flex>
        }
        options={[
          {
            label: `${values.in_hours === 'false' ? 'Days' : 'Hours'} will continue to accrue, regardless of balance`,
            value: '0',
          },
          { label: 'Accrual stops at a certain balance', value: '1' },
        ]}
        radioProps={{
          colorScheme: 'secondary',
        }}
        name="set_max_balance"
      />
      {values.set_max_balance === '1' && (
        <Flex gridGap="2" mb="7" pl="6">
          <Text mt="4"> Stop accruing time when the balance reaches</Text>
          <FormikInput name="max_balance" formControlProps={{ minWidth: '64px', width: '64px', mb: 0 }} />
          <Text mt="4"> {values.in_hours === 'false' ? 'days' : 'hours'}.</Text>
        </Flex>
      )}
      <FormikRadio
        label={
          <Flex alignItems="center">
            Carryover expiration
            <Tooltip
              label={`Determines whether unused leave ${
                values.in_hours === 'false' ? 'days' : 'hours'
              } will expire after a certain period of carry over to the next year.`}
            >
              <Box ml="2">
                <Icon as={HiOutlineQuestionMarkCircle} height="18px" width="18px" color="gray.400" />
              </Box>
            </Tooltip>
          </Flex>
        }
        options={[
          {
            label: `Any unused ${values.in_hours === 'false' ? 'days' : 'hours'} will expire — "use it or lose it"`,
            value: '1',
          },
          { label: 'Unused time off can be carried over to the next period', value: '0' },
        ]}
        radioProps={{
          colorScheme: 'secondary',
        }}
        name="reset_balance_yearly"
      />
      {values.reset_balance_yearly === '0' && (
        <Flex gridGap="2" mb="7">
          <Text mt="4"> The next period can carry over</Text>
          <FormikSelect
            name="carryover_expiration_type"
            options={[
              { label: 'Up to...', value: 'up-to' },
              { label: 'Unlimited', value: 'unlimited' },
            ]}
            selectProps={{ isClearable: false, placeholder: '' }}
            formControlProps={{ minWidth: '130px', width: '130px', mb: 0 }}
          />
          {values.carryover_expiration_type?.value === 'up-to' && (
            <FormikInput name="max_carryover_balance" formControlProps={{ minWidth: '64px', width: '64px', mb: 0 }} />
          )}
          <Text mt="4"> {values.in_hours === 'false' ? 'days' : 'hours'}.</Text>
        </Flex>
      )}

      <FormLabel
        htmlFor="initial_balance"
        color="gray.500"
        fontSize="12px"
        fontWeight="600"
        mb="2"
        textTransform="uppercase"
      >
        Initial Time Off Balance
      </FormLabel>
      <Flex gridGap="3" mb="5">
        <FormikInput name="initial_balance" formControlProps={{ minWidth: '80px', width: '80px', mb: 0 }} />
        <Text mt="4"> {values.in_hours === 'false' ? 'days' : 'hours'}</Text>
      </Flex>

      <Box mb="4">
        <FormLabel htmlFor="active" color="gray.500" fontSize="12px" fontWeight="600" mb="2" textTransform="uppercase">
          Allow negative balance ?
        </FormLabel>
        <FormikSwitch name="allow_negative_balance" />
      </Box>
    </Box>
  );
};

export default Accrual;
